/**
 * Hydrates the independent Home "Recently Opened" and "Saved topics"
 * member sections.
 *
 * Sources:
 *   /api/topics/recent         — canonical Browse Recent topic list,
 *                                 with each topic's latest progress
 *   /api/user/favorites        — favorited topic list
 *
 * The previous markup burned in 4 fake recent cards (LLMs / Zero
 * to First Revenue / Rust / RAG) with hardcoded progress percents
 * and a hardcoded `showTopicDetail('llms')` href. This module
 * replaces them.
 */
import { byId } from "./domUtils";
import type { Root } from "./prototypeTypes";
import type { PrototypeApiClient } from "./apiClient";
import { DEFAULT_COVER_URL } from '../../domain/topicCover';
import { syncHomeRecentPlaybackControls } from './homeRecentPlaybackPresentation';
import { mountedFor } from './mediaPlayer';
import { homeRecentAudioPlaybackState } from './mediaPlayerProgress';
import { buildTopicCard } from './topicCard';
import { applyCompactTextBudget, observeCompactTextBudget } from './compactTextBudget';
import type { TopicSource } from '../../domain/types/contentTypes';
import type { BrowseCoverBadge } from '../../domain/topicCoverBadge';
import type { PublicTopicSummary } from './apiTopicContracts';

interface HomeListLoad {
	generation: number;
	controller: AbortController;
}

const homeListLoads = new WeakMap<HTMLElement, HomeListLoad>();
const homeFavoriteReconciliations = new WeakMap<HTMLElement, number>();
const homeRecentReconciliations = new WeakMap<HTMLElement, number>();
const homeRecentTextBudgetObservers = new WeakMap<HTMLElement, () => void>();
export const HOME_LIBRARY_ITEM_LIMIT = 20;
export type HomeRecentTopicsResponse = Awaited<ReturnType<PrototypeApiClient['topicRecent']>>;

/** Cancels hidden Home library paints when navigation leaves Home. */
export function deactivateHomeRecentAndFavorites(root: Root): void {
	for (const id of ['recent-list', 'favorites-list']) {
		const list = byId(root, id);
		if (!list) continue;
		homeListLoads.get(list)?.controller.abort();
		if (id === 'recent-list') {
			homeRecentTextBudgetObservers.get(list)?.();
			homeRecentTextBudgetObservers.delete(list);
		}
		invalidateHomeReconciliations(list);
	}
	syncMobileHomeLibrary(root);
}

export async function hydrateHomeRecent(
  root: Root,
  api: PrototypeApiClient,
	recentRequest?: Promise<HomeRecentTopicsResponse>,
): Promise<void> {
  const list = byId(root, 'recent-list');
  if (list) invalidateHomeRecentReconciliations(list);
  await hydrateRecentList(root, api, recentRequest);
}

export async function hydrateHomeFavorites(
  root: Root,
  api: PrototypeApiClient,
): Promise<void> {
	const list = byId(root, 'favorites-list');
	if (list) invalidateHomeFavoriteReconciliations(list);
  await hydrateFavoritesList(root, api);
}

/**
 * Refresh Saved after a favorite write without putting the Home rail back
 * through its loading state. Existing cards are retained by slug so a mobile
 * rail keeps its scroll position and never flashes its skeletons.
 */
export async function reconcileHomeFavorites(root: Root, api: PrototypeApiClient): Promise<void> {
	const list = byId(root, 'favorites-list');
	const empty = byId(root, 'favoritesEmpty');
	if (!list) return;
	const reconciliation = (homeFavoriteReconciliations.get(list) ?? 0) + 1;
	homeFavoriteReconciliations.set(list, reconciliation);
	const scrollLeft = list.scrollLeft;
	try {
		// Do not abort a first-frame Saved request before this silent request has
		// actually succeeded. If the reconciliation request fails, the original
		// hydrator remains responsible for replacing its skeletons with usable
		// content or its own error state.
		const items = await loadHomeFavoriteEntries(api);
		if (homeFavoriteReconciliations.get(list) !== reconciliation) return;
		const load = beginHomeListLoad(list);
		reconcileFavoriteCards(list, items);
		restoreHomeListScroll(list, load, scrollLeft);
		showHomeLibraryResult(list, empty, items.length > 0);
		syncMobileHomeLibrary(root);
	} catch {
		// The favorite write already succeeded. Keep the currently visible result
		// in place for a later successful reconciliation instead of making this
		// persistent section jump to another loading or error treatment.
	}
}

/**
 * A confirmed un-save is already authoritative. Remove that Saved card before
 * the optional read-back so a transient GET failure cannot leave a card that
 * the user has just removed.
 */
export function removeHomeFavoriteCard(root: Root, topicSlug: string): void {
	const list = byId(root, 'favorites-list');
	const empty = byId(root, 'favoritesEmpty');
	if (!list) return;
	const card = Array.from(list.querySelectorAll<HTMLElement>('.home-saved-topic-card')).find((candidate) => candidate.dataset.id === topicSlug);
	if (!card) return;
	const group = card.closest<HTMLElement>('.home-library-group');
	card.remove();
	if (group && group.children.length === 0) group.remove();
	showHomeLibraryResult(list, empty, list.querySelector('.home-saved-topic-card') !== null);
	syncMobileHomeLibrary(root);
}

/**
 * Refresh Recently after a confirmed progress write or a stable Home return.
 * This follows the Saved reconciliation contract: do not show a loading state,
 * retain cards by topic slug, and preserve the rail's current scroll position.
 */
export async function reconcileHomeRecent(root: Root, api: PrototypeApiClient): Promise<void> {
	const list = byId(root, 'recent-list');
	const empty = byId(root, 'recentEmpty');
	if (!list) return;
	const reconciliation = (homeRecentReconciliations.get(list) ?? 0) + 1;
	homeRecentReconciliations.set(list, reconciliation);
	const scrollLeft = list.scrollLeft;
	try {
		const response = await api.topicRecent({ limit: HOME_LIBRARY_ITEM_LIMIT });
		if (homeRecentReconciliations.get(list) !== reconciliation) return;
		const load = beginHomeListLoad(list);
		const items = recentEntriesFromResponse(response);
		reconcileRecentCards(list, items);
		restoreHomeListScroll(list, load, scrollLeft);
		showHomeLibraryResult(list, empty, items.length > 0);
		const mountedAudio = root.querySelector?.('#webPlayer') ? mountedFor(root, 'audio') : undefined;
		syncHomeRecentPlaybackControls(root, mountedAudio ? homeRecentAudioPlaybackState(root, mountedAudio) : null);
		syncMobileHomeLibrary(root);
		// The Home hero tells the same continue-here story as the rail: feed it
		// the same fresh snapshot so returning to Home never shows a stale topic.
		void import('./homeMemberHydrator').then((member) => member.refreshHeroResumeFromRecent(root, response)).catch(() => undefined);
	} catch {
		// A completed progress write must not turn a populated rail into an empty
		// treatment just because its optional read-back failed.
	}
}

export async function hydrateHomeRecentAndFavorites(
  root: Root,
  api: PrototypeApiClient,
	recentRequest?: Promise<HomeRecentTopicsResponse>,
): Promise<void> {
  await Promise.allSettled([
    hydrateHomeRecent(root, api, recentRequest),
    hydrateHomeFavorites(root, api),
  ]);
	syncMobileHomeLibrary(root);
}

/**
 * Phones merge the two personal rails into a single text-tab surface while
 * retaining the original desktop DOM and the existing two-card snap groups.
 * Both layouts use the settled card state: desktop hides each empty rail,
 * while phones expose whichever non-empty rail is available in the tab host.
 * The move happens only after both requests settle, so an empty Recent never
 * flashes before an available Saved result (and vice versa).
 */
export function syncMobileHomeLibrary(root: Root): void {
	const view = root.ownerDocument?.defaultView ?? (typeof window !== 'undefined' ? window : null);
	const recentSection = byId(root, 'recentSection');
	const savedSection = byId(root, 'savedSection');
	const host = byId(root, 'mobileHomeLibrarySection');
	const recentContent = byId(root, 'recentContentSection');
	const savedContent = byId(root, 'favoritesContentSection');
	const recentList = byId(root, 'recent-list');
	const savedList = byId(root, 'favorites-list');
	const recentPanel = byId(root, 'mobileRecentPanel');
	const savedPanel = byId(root, 'mobileSavedPanel');
	const recentTab = byId(root, 'mobileRecentTab');
	const savedTab = byId(root, 'mobileSavedTab');
	const recentViewAll = byId(root, 'mobileRecentViewAll');
	const savedViewAll = byId(root, 'mobileSavedViewAll');
	if (!view || !recentSection || !savedSection || !host || !recentContent || !savedContent || !recentList || !savedList || !recentPanel || !savedPanel || !recentTab || !savedTab) return;
	// Bind before the desktop early return so a viewport rotation can move the
	// already-settled personal rails into their phone tabs without reloading.
	if (host.dataset.layoutBound !== 'true') {
		host.dataset.layoutBound = 'true';
		view.matchMedia?.('(max-width: 640px)').addEventListener('change', () => syncMobileHomeLibrary(root));
	}
	const mobile = view.matchMedia?.('(max-width: 640px)').matches ?? view.innerWidth <= 640;
	const hasRecent = recentList.querySelector('.recent-card') !== null;
	const hasSaved = savedList.querySelector('.home-saved-topic-card') !== null;
	if (!mobile) {
		if (recentContent.parentElement !== recentSection) recentSection.appendChild(recentContent);
		if (savedContent.parentElement !== savedSection) savedSection.appendChild(savedContent);
		recentSection.hidden = !hasRecent;
		savedSection.hidden = !hasSaved;
		host.hidden = true;
		return;
	}
	if (!hasRecent && !hasSaved) {
		host.hidden = true;
		if (recentViewAll) recentViewAll.hidden = true;
		if (savedViewAll) savedViewAll.hidden = true;
		return;
	}
	host.hidden = false;
	if (recentContent.parentElement !== recentPanel) recentPanel.appendChild(recentContent);
	if (savedContent.parentElement !== savedPanel) savedPanel.appendChild(savedContent);
	const selected = host.dataset.selected === 'saved' && hasSaved ? 'saved' : hasRecent ? 'recent' : 'saved';
	host.dataset.selected = selected;
	recentTab.hidden = !hasRecent;
	savedTab.hidden = !hasSaved;
	for (const [key, tab, panel] of [['recent', recentTab, recentPanel], ['saved', savedTab, savedPanel]] as const) {
		const active = key === selected;
		tab.setAttribute('aria-selected', String(active));
		tab.tabIndex = active ? 0 : -1;
		panel.hidden = !active;
	}
	if (recentViewAll) recentViewAll.hidden = selected !== 'recent';
	if (savedViewAll) savedViewAll.hidden = selected !== 'saved';
	if (host.dataset.tabsBound !== 'true') {
		host.dataset.tabsBound = 'true';
		const activate = (key: 'recent' | 'saved') => { host.dataset.selected = key; syncMobileHomeLibrary(root); };
		recentTab.addEventListener('click', () => activate('recent'));
		savedTab.addEventListener('click', () => activate('saved'));
		host.addEventListener('keydown', (event) => {
			if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
			const options = [recentTab, savedTab].filter((tab) => !tab.hidden);
			const current = options.indexOf(event.target as HTMLButtonElement);
			if (current < 0) return;
			event.preventDefault();
			const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length;
			options[next]?.focus(); options[next]?.click();
		});
	}
}

interface RecentEntry {
  chapterId: string;
  topicSlug: string;
  topicTitle: string;
  chapterIndex: number;
  chapterTitle: string;
	progressPercent: number;
	mediaResumePositionSeconds?: number | null;
	audioDurationSeconds?: number | null;
  status: string;
  updatedAt: string;
  coverIcon: string | null;
  coverGradient: string | null;
	coverImageUrl?: string;
	source?: TopicSource;
	coverBadge?: BrowseCoverBadge;
}

interface FavoriteEntry {
  topicSlug: string;
  topicTitle: string;
	topicSubtitle?: string | null;
  coverIcon: string | null;
  coverGradient: string | null;
  category: string | null;
  starredAt: string;
	coverImageUrl?: string;
	voteValue?: number;
	source?: TopicSource;
	coverBadge?: BrowseCoverBadge;
}

async function hydrateRecentList(root: Root, api: PrototypeApiClient, recentRequest?: Promise<HomeRecentTopicsResponse>): Promise<void> {
  const list = byId(root, "recent-list");
  const empty = byId(root, "recentEmpty");
	if (!list) return;
	const load = beginHomeListLoad(list);
	const scrollLeft = list.scrollLeft;
	showHomeLibraryResult(list, empty, list.querySelector('.recent-card') !== null, false);
  try {
		const resp = await (recentRequest ?? api.topicRecent({ limit: HOME_LIBRARY_ITEM_LIMIT }, load.controller.signal));
		if (!isCurrentHomeListLoad(list, load)) return;
		const items = recentEntriesFromResponse(resp);
		if (!isCurrentHomeListLoad(list, load)) return;
		if (items.length === 0) {
			list.replaceChildren();
			showHomeLibraryResult(list, empty, false);
      return;
    }
		list.replaceChildren(...buildLibraryGroups(items, buildRecentCard));
		observeRecentTextBudget(list);
		// Home is also hydrated in isolated documents without the global player
		// shell. Do not create its fallback host just to paint an empty state.
		const mountedAudio = root.querySelector?.('#webPlayer') ? mountedFor(root, 'audio') : undefined;
		syncHomeRecentPlaybackControls(root, mountedAudio ? homeRecentAudioPlaybackState(root, mountedAudio) : null);
		restoreHomeListScroll(list, load, scrollLeft);
		showHomeLibraryResult(list, empty, true);
	} catch {
		if (!isCurrentHomeListLoad(list, load)) return;
		list.replaceChildren();
		showHomeLibraryResult(list, empty, false);
	}
}

async function hydrateFavoritesList(root: Root, api: PrototypeApiClient): Promise<void> {
  const list = byId(root, "favorites-list");
  const empty = byId(root, "favoritesEmpty");
	if (!list) return;
	const load = beginHomeListLoad(list);
	const scrollLeft = list.scrollLeft;
	showHomeLibraryResult(list, empty, list.querySelector('.home-saved-topic-card') !== null, false);
	try {
		const items = await loadHomeFavoriteEntries(api, load.controller.signal);
		if (!isCurrentHomeListLoad(list, load)) return;
		if (!isCurrentHomeListLoad(list, load)) return;
		if (items.length === 0) {
			list.replaceChildren();
			showHomeLibraryResult(list, empty, false);
      return;
    }
    list.replaceChildren(...buildLibraryGroups(items, buildFavoriteCard));
		restoreHomeListScroll(list, load, scrollLeft);
		showHomeLibraryResult(list, empty, true);
	} catch {
		if (!isCurrentHomeListLoad(list, load)) return;
		list.replaceChildren();
		showHomeLibraryResult(list, empty, false);
	}
}

/**
 * Keep the result and its desktop section in lockstep. This also ensures a
 * cleared or empty rail cannot briefly leave its header visible while another
 * Home request is still in flight.
 */
function showHomeLibraryResult(list: HTMLElement, empty: HTMLElement | null, hasItems: boolean, settled = true): void {
	list.style.display = hasItems ? '' : 'none';
	if (empty) empty.style.display = hasItems ? 'none' : '';
	const sectionId = list.id === 'recent-list' ? 'recentSection' : list.id === 'favorites-list' ? 'savedSection' : null;
	const section = sectionId ? list.ownerDocument.getElementById(sectionId) : null;
	if (section) {
		section.hidden = !hasItems;
		section.dataset.homeLibraryState = settled && hasItems ? 'ready' : 'loading';
	}
}

function beginHomeListLoad(list: HTMLElement): HomeListLoad {
	const previous = homeListLoads.get(list);
	previous?.controller.abort();
	const load = { generation: (previous?.generation ?? 0) + 1, controller: new AbortController() };
	homeListLoads.set(list, load);
	return load;
}

/** A fresh Saved hydration represents a new Home/viewer scope. */
function invalidateHomeFavoriteReconciliations(list: HTMLElement): void {
	homeFavoriteReconciliations.set(list, (homeFavoriteReconciliations.get(list) ?? 0) + 1);
}

function invalidateHomeRecentReconciliations(list: HTMLElement): void {
	homeRecentReconciliations.set(list, (homeRecentReconciliations.get(list) ?? 0) + 1);
}

function invalidateHomeReconciliations(list: HTMLElement): void {
	invalidateHomeFavoriteReconciliations(list);
	invalidateHomeRecentReconciliations(list);
}

function isCurrentHomeListLoad(list: HTMLElement, load: HomeListLoad): boolean {
	const current = homeListLoads.get(list);
	return current === load && !load.controller.signal.aborted;
}

/** Keep an in-progress mobile rail in place when favorite writes rehydrate it. */
function restoreHomeListScroll(list: HTMLElement, load: HomeListLoad, scrollLeft: number): void {
	list.scrollLeft = scrollLeft;
	const view = list.ownerDocument.defaultView;
	if (typeof view?.requestAnimationFrame !== 'function') return;
	view.requestAnimationFrame(() => {
		if (isCurrentHomeListLoad(list, load)) list.scrollLeft = scrollLeft;
	});
}

function buildLibraryGroups<T>(items: T[], buildCard: (item: T) => HTMLElement): HTMLElement[] {
	const groups: HTMLElement[] = [];
	for (let index = 0; index < items.length; index += 2) {
		const group = document.createElement('div');
		group.className = 'home-library-group';
		group.dataset.homeLibraryGroup = String(index / 2);
		group.replaceChildren(...items.slice(index, index + 2).map(buildCard));
		groups.push(group);
	}
	return groups;
}

function reconcileFavoriteCards(list: HTMLElement, items: FavoriteEntry[]): void {
	for (const child of Array.from(list.children)) {
		if (!child.classList.contains('home-library-group')) child.remove();
	}
	const existingCards = new Map(Array.from(list.querySelectorAll<HTMLElement>(':scope .home-saved-topic-card')).map((card) => [card.dataset.id, card]));
	const cards = items.map((entry) => {
		const card = existingCards.get(entry.topicSlug);
		return card ? updateFavoriteCard(card, entry) : buildFavoriteCard(entry);
	});
	const groups = new Map(Array.from(list.querySelectorAll<HTMLElement>(':scope > .home-library-group')).map((group) => [group.dataset.homeLibraryGroup, group]));
	const desiredGroups: HTMLElement[] = [];
	for (let index = 0; index < cards.length; index += 2) {
		const groupIndex = String(index / 2);
		const group = groups.get(groupIndex) ?? document.createElement('div');
		group.className = 'home-library-group';
		group.dataset.homeLibraryGroup = groupIndex;
		syncChildren(group, cards.slice(index, index + 2));
		desiredGroups.push(group);
	}
	for (const group of Array.from(list.querySelectorAll<HTMLElement>(':scope > .home-library-group'))) {
		if (!desiredGroups.includes(group)) group.remove();
	}
	for (let index = 0; index < desiredGroups.length; index += 1) {
		const group = desiredGroups[index]!;
		if (list.children[index] !== group) list.insertBefore(group, list.children[index] ?? null);
	}
}

function reconcileRecentCards(list: HTMLElement, items: RecentEntry[]): void {
	for (const child of Array.from(list.children)) {
		if (!child.classList.contains('home-library-group')) child.remove();
	}
	const existingCards = new Map(Array.from(list.querySelectorAll<HTMLElement>(':scope .recent-card')).map((card) => [card.dataset.id, card]));
	const cards = items.map((entry) => {
		const card = existingCards.get(entry.topicSlug);
		return card ? updateRecentCard(card, entry) : buildRecentCard(entry);
	});
	reconcileLibraryGroups(list, cards);
	observeRecentTextBudget(list);
}

function reconcileLibraryGroups(list: HTMLElement, cards: HTMLElement[]): void {
	const groups = new Map(Array.from(list.querySelectorAll<HTMLElement>(':scope > .home-library-group')).map((group) => [group.dataset.homeLibraryGroup, group]));
	const desiredGroups: HTMLElement[] = [];
	for (let index = 0; index < cards.length; index += 2) {
		const groupIndex = String(index / 2);
		const group = groups.get(groupIndex) ?? document.createElement('div');
		group.className = 'home-library-group';
		group.dataset.homeLibraryGroup = groupIndex;
		syncChildren(group, cards.slice(index, index + 2));
		desiredGroups.push(group);
	}
	for (const group of Array.from(list.querySelectorAll<HTMLElement>(':scope > .home-library-group'))) {
		if (!desiredGroups.includes(group)) group.remove();
	}
	for (let index = 0; index < desiredGroups.length; index += 1) {
		const group = desiredGroups[index]!;
		if (list.children[index] !== group) list.insertBefore(group, list.children[index] ?? null);
	}
}

function syncChildren(parent: HTMLElement, desired: HTMLElement[]): void {
	for (const child of Array.from(parent.children)) {
		if (!desired.includes(child as HTMLElement)) child.remove();
	}
	for (let index = 0; index < desired.length; index += 1) {
		const card = desired[index]!;
		if (parent.children[index] !== card) parent.insertBefore(card, parent.children[index] ?? null);
	}
}

function buildRecentCard(entry: RecentEntry): HTMLElement {
  const card = document.createElement("div");
  card.className = "recent-card recent-card--chapter-two-lines";
  card.tabIndex = 0;
  card.setAttribute('role', 'link');
  const completed = isRecentEntryCompleted(entry);
  card.setAttribute('aria-label', `Continue ${entry.topicTitle}: ${recentChapterLabel(entry)}`);
  card.dataset.keyboardAction = 'true';
	card.dataset.id = entry.topicSlug;
  card.dataset.homeRecentTopicSlug = entry.topicSlug;
  card.dataset.homeRecentChapterId = entry.chapterId;
  card.dataset.homeRecentChapterIndex = String(entry.chapterIndex);
	card.dataset.homeRecentChapterTitle = entry.chapterTitle;
	card.dataset.homeRecentTopicTitle = entry.topicTitle;
	if (entry.source) card.dataset.source = entry.source;
	if (completed) card.dataset.homeRecentCompleted = '1';
	const audioResumePercent = completed ? 100 : resumePercent(entry);
  card.dataset.homeRecentProgressPercent = String(audioResumePercent);
  if (typeof entry.mediaResumePositionSeconds === 'number') card.dataset.homeRecentPositionSeconds = String(entry.mediaResumePositionSeconds);
  card.setAttribute('data-proto-click', 'openHomeRecentChapter()');
  const thumbProgress = document.createElement('div');
  thumbProgress.className = 'recent-thumb-progress wp-thumb-progress';
  thumbProgress.setAttribute('aria-hidden', 'true');
	thumbProgress.style.setProperty('--audio-progress', `${audioResumePercent}%`);
  const thumb = document.createElement("div");
	thumb.className = `recent-thumb ${entry.coverGradient || "grad-ai"} recent-thumb--default`;
	thumb.style.backgroundImage = `url("${entry.coverImageUrl ?? DEFAULT_COVER_URL}")`;
	paintRecentCoverBadge(thumb, entry.coverBadge);
  thumbProgress.appendChild(thumb);
  card.appendChild(thumbProgress);
  const info = document.createElement("div");
  info.className = "recent-info";
  const h5 = document.createElement("h5");
  h5.textContent = recentChapterLabel(entry);
  info.appendChild(h5);
  const p = document.createElement("p");
  p.textContent = entry.topicTitle;
  info.appendChild(p);
  card.appendChild(info);
  const resume = document.createElement('button');
  resume.type = 'button';
  resume.className = 'recent-resume';
  resume.title = 'Play';
	resume.setAttribute('aria-label', `Play ${entry.topicTitle}: ${recentChapterLabel(entry)}`);
	resume.setAttribute('aria-pressed', 'false');
  resume.setAttribute('data-proto-click', 'playHomeRecentChapter()');
  resume.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>';
  card.appendChild(resume);
  return card;
}

function buildFavoriteCard(entry: FavoriteEntry): HTMLElement {
	const card = buildTopicCard({
		slug: entry.topicSlug,
		title: entry.topicTitle,
		subtitle: entry.topicSubtitle || entry.category || 'Topic',
		category: entry.category,
		coverImageUrl: entry.coverImageUrl ?? DEFAULT_COVER_URL,
		coverGradient: entry.coverGradient,
		source: entry.source,
		coverBadge: entry.coverBadge,
		isFavorite: true,
		voteValue: entry.voteValue ?? 0,
	}, { showSubtitle: true, variant: 'mobile-compact', reactionActions: 'like-save' });
	card.classList.add('home-saved-topic-card');
	card.dataset.starredAt = entry.starredAt;
	return card;
}

function updateFavoriteCard(card: HTMLElement, entry: FavoriteEntry): HTMLElement {
	// Rebuild through the shared Topic-card builder rather than hand-patching a
	// second DOM contract. syncChildren removes the old root before inserting
	// this replacement, so a reconcile cannot leave duplicate or stale actions.
	return buildFavoriteCard(entry);
}

function updateRecentCard(card: HTMLElement, entry: RecentEntry): HTMLElement {
	const completed = isRecentEntryCompleted(entry);
	card.setAttribute('aria-label', `Continue ${entry.topicTitle}: ${recentChapterLabel(entry)}`);
	card.dataset.homeRecentTopicSlug = entry.topicSlug;
	card.dataset.homeRecentChapterId = entry.chapterId;
	card.dataset.homeRecentChapterIndex = String(entry.chapterIndex);
	card.dataset.homeRecentChapterTitle = entry.chapterTitle;
	card.dataset.homeRecentTopicTitle = entry.topicTitle;
	if (entry.source) card.dataset.source = entry.source;
	else delete card.dataset.source;
	if (completed) card.dataset.homeRecentCompleted = '1';
	else delete card.dataset.homeRecentCompleted;
	const audioResumePercent = completed ? 100 : resumePercent(entry);
	card.dataset.homeRecentProgressPercent = String(audioResumePercent);
	if (typeof entry.mediaResumePositionSeconds === 'number') card.dataset.homeRecentPositionSeconds = String(entry.mediaResumePositionSeconds);
	else delete card.dataset.homeRecentPositionSeconds;
	card.querySelector<HTMLElement>('.recent-thumb-progress')?.style.setProperty('--audio-progress', `${audioResumePercent}%`);
	const thumb = card.querySelector<HTMLElement>('.recent-thumb');
	if (thumb) {
		thumb.className = `recent-thumb ${entry.coverGradient || 'grad-ai'} recent-thumb--default`;
		thumb.style.backgroundImage = `url("${entry.coverImageUrl ?? DEFAULT_COVER_URL}")`;
		paintRecentCoverBadge(thumb, entry.coverBadge);
	}
	const title = card.querySelector<HTMLElement>('.recent-info h5');
	if (title) title.textContent = recentChapterLabel(entry);
	const subtitle = card.querySelector<HTMLElement>('.recent-info p');
	if (subtitle) subtitle.textContent = entry.topicTitle;
	const resume = card.querySelector<HTMLElement>('.recent-resume');
	if (resume) resume.setAttribute('aria-label', `Play ${entry.topicTitle}: ${recentChapterLabel(entry)}`);
	syncRecentTextBudget(card);
	return card;
}

function recentEntriesFromResponse(response: Awaited<ReturnType<PrototypeApiClient['topicRecent']>>): RecentEntry[] {
	return (response.topics ?? []).flatMap((topic) => {
		const progress = topic.latestProgress;
		if (!progress) return [];
		return [{
			chapterId: progress.chapterId,
			topicSlug: topic.slug,
			topicTitle: topic.title,
			chapterIndex: progress.chapterIndex,
			chapterTitle: progress.chapterTitle,
			status: progress.status,
			progressPercent: progress.progressPercent,
			mediaResumePositionSeconds: progress.mediaResumePositionSeconds,
			audioDurationSeconds: progress.audioDurationSeconds,
			updatedAt: progress.updatedAt,
			coverIcon: topic.coverIcon ?? null,
			coverGradient: topic.coverGradient ?? null,
			coverImageUrl: topic.coverImageUrl,
			source: topic.source,
			coverBadge: topic.coverBadge,
		}];
	}).slice(0, HOME_LIBRARY_ITEM_LIMIT);
}

async function loadHomeFavoriteEntries(api: PrototypeApiClient, signal?: AbortSignal): Promise<FavoriteEntry[]> {
	const detailed = api as PrototypeApiClient & {
		listFavoritesWithTopics?: (options: number, requestSignal?: AbortSignal) => Promise<Awaited<ReturnType<PrototypeApiClient['listFavorites']>> & { topics?: PublicTopicSummary[] }>;
	};
	const response: Awaited<ReturnType<PrototypeApiClient['listFavorites']>> & { topics?: PublicTopicSummary[] } = detailed.listFavoritesWithTopics
		? await detailed.listFavoritesWithTopics(HOME_LIBRARY_ITEM_LIMIT, signal)
		: await api.listFavorites(HOME_LIBRARY_ITEM_LIMIT, signal);
	const topicsBySlug = new Map((response.topics ?? []).map((topic) => [topic.slug, topic]));
	return [...(response.favorites ?? [])]
		.map((favorite) => {
			const topic = topicsBySlug.get(favorite.topicSlug);
			return {
				topicSlug: favorite.topicSlug,
				topicTitle: favorite.topicTitle,
				topicSubtitle: favorite.topicSubtitle,
				coverIcon: favorite.coverIcon ?? null,
				coverGradient: favorite.coverGradient ?? null,
				category: favorite.category ?? null,
				starredAt: favorite.starredAt,
				coverImageUrl: favorite.coverImageUrl ?? topic?.coverImageUrl,
				voteValue: favorite.voteValue ?? topic?.voteValue,
				source: topic?.source,
				coverBadge: topic?.coverBadge,
			} satisfies FavoriteEntry;
		})
		.sort((a, b) => time(b.starredAt) - time(a.starredAt))
		.slice(0, HOME_LIBRARY_ITEM_LIMIT);
}

function paintRecentCoverBadge(thumb: HTMLElement, coverBadge: BrowseCoverBadge | undefined): void {
	thumb.querySelector('.recent-cover-badge')?.remove();
	if (!coverBadge) return;
	const badge = thumb.ownerDocument.createElement('span');
	badge.className = `recent-cover-badge cover-badge topic-cover-badge browse-cover-badge browse-cover-badge--${coverBadge}${coverBadge === 'github' ? ' source-badge' : ''}`;
	badge.textContent = coverBadge === 'github' ? 'GitHub' : coverBadge === 'trending' ? 'Trending' : 'New';
	thumb.appendChild(badge);
}

function recentChapterLabel(entry: Pick<RecentEntry, 'chapterIndex' | 'chapterTitle'>): string {
	return `Ch.${entry.chapterIndex} · ${entry.chapterTitle}`;
}

function normalizeProgress(value: number): number {
	return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
}

function resumePercent(entry: Pick<RecentEntry, 'progressPercent' | 'mediaResumePositionSeconds' | 'audioDurationSeconds'>): number {
	if (typeof entry.mediaResumePositionSeconds === 'number' && typeof entry.audioDurationSeconds === 'number' && entry.audioDurationSeconds > 0) {
		return normalizeProgress((entry.mediaResumePositionSeconds / entry.audioDurationSeconds) * 100);
	}
	return 0;
}

/**
 * A chapter whose audio ran to the end keeps its rail slot with its ring
 * closed at 100%. `status`/`progressPercent` are deliberate high-water marks:
 * a fresh partial re-listen of a formerly completed chapter must keep that
 * history, so they cannot tell whether the chapter is finished *now*. The
 * dedicated resume position is the only signal that reflects the current
 * session — the jump-to-next decision follows it, and `status` only covers
 * rows persisted without a usable position/duration pair. 98% is the
 * product-wide "listened through" threshold, matching MEDIA_COMPLETED_RATIO
 * in mediaPlayerProgress.
 */
function isRecentEntryCompleted(entry: Pick<RecentEntry, 'status' | 'progressPercent' | 'mediaResumePositionSeconds' | 'audioDurationSeconds'>): boolean {
	if (typeof entry.mediaResumePositionSeconds === 'number' && typeof entry.audioDurationSeconds === 'number' && entry.audioDurationSeconds > 0) {
		return resumePercent(entry) >= 98;
	}
	return entry.status === 'completed' || entry.progressPercent >= 98;
}

const pendingEndedReconciles = new WeakMap<object, ReturnType<typeof setTimeout>>();

/**
 * Finishing a chapter persists its completed progress asynchronously and
 * bumps the topic's recency, so the visible rail must follow without waiting
 * for the user to leave and re-enter Home. The delay lets the ended write
 * land first; the rail read tolerates a marginally early one because
 * near-end progress rounds up to the completed ring.
 */
export function scheduleHomeRecentReconcile(root: Root, api: PrototypeApiClient, delayMs = 2000): void {
	if (typeof setTimeout === 'undefined') return;
	const key = root as unknown as object;
	const existing = pendingEndedReconciles.get(key);
	if (existing) clearTimeout(existing);
	pendingEndedReconciles.set(key, setTimeout(() => {
		pendingEndedReconciles.delete(key);
		void reconcileHomeRecent(root, api);
	}, delayMs));
}

/**
 * The dedicated Recent card keeps a fixed three-line text budget: a wrapped
 * chapter title leaves one line for the Topic subtitle, otherwise it leaves
 * two. Each title is observed because late font settlement can alter its
 * wrapped line count without changing the list's own dimensions.
 */
function observeRecentTextBudget(list: HTMLElement): void {
	homeRecentTextBudgetObservers.get(list)?.();
	const cleanups = Array.from(list.querySelectorAll<HTMLElement>('.recent-card')).flatMap((card) => {
		const title = card.querySelector<HTMLElement>('.recent-info h5');
		return title ? [observeCompactTextBudget(card, title, 'recent-card--chapter-two-lines')] : [];
	});
	homeRecentTextBudgetObservers.set(list, () => cleanups.forEach((cleanup) => cleanup()));
}

function syncRecentTextBudget(card: HTMLElement): void {
	const title = card.querySelector<HTMLElement>('.recent-info h5');
	if (!title) return;
	applyCompactTextBudget(card, title, 'recent-card--chapter-two-lines');
}

function time(value: string | undefined): number {
  const n = Date.parse(value ?? "");
  return Number.isFinite(n) ? n : 0;
}
